import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { DayCalendar } from '@components/pages/Appointments/index'
import type { IAppointmentDetails } from '@components/pages/Appointments/DayCalendar/DayCalendar.component'
import type { RootState } from '@store/index'
import type { AppointmentResponse } from '@dataTypes/appointment.type'
import { clinicIdsFromProvider } from '@utils/staffClinicScope.util'
import { getMockOperatoriesForOverview } from './dashboardOverview.mockData'
import { isDashboardDevMocksEnabled } from '@utils/dashboardDevMocks.util'

type OverviewTodaySchedulePanelProps = {
  date: string
  providerKey: string
  enabled?: boolean
  appointments: AppointmentResponse[] | null
}

/**
 * Owns the today-schedule fetch + DayCalendar so that list does not live in
 * the shared appointment store used by the appointments page.
 */
function OverviewTodaySchedulePanelImpl({
  date,
  providerKey,
  enabled = true,
  appointments,
}: OverviewTodaySchedulePanelProps): React.ReactElement | null {
  const useMocks = isDashboardDevMocksEnabled()
  const appointmentList = appointments
  const providersFromStore = useSelector(
    (state: RootState) => state.provider.data,
  )
  const selectedClinicId = useSelector(
    (state: RootState) => state.clinic.selectedClinic?.clinic_id,
  )
  const operatoryList = useSelector((state: RootState) => state.operatory.data)
  const operatoryLoading = useSelector(
    (state: RootState) => state.operatory.loading,
  )
  const isHIPAAView = useSelector((state: RootState) => state.ui.isHIPAAView)

  const [appointmentDetails, setAppointmentDetails] =
    useState<IAppointmentDetails>({
      startTime: '',
      endTime: '',
      room: '',
      provider: '',
    })
  void appointmentDetails

  const providerList = useMemo(() => {
    const providers = providersFromStore ?? []
    if (!selectedClinicId) return providers
    return providers.filter((provider) =>
      clinicIdsFromProvider(provider).includes(selectedClinicId),
    )
  }, [providersFromStore, selectedClinicId])

  const displayOperatoryList = useMemo(() => {
    if ((operatoryList?.length ?? 0) > 0) return operatoryList ?? []
    return useMocks ? getMockOperatoriesForOverview() : []
  }, [operatoryList, useMocks])

  const [operatoriesSettled, setOperatoriesSettled] = useState(
    () => (operatoryList?.length ?? 0) > 0,
  )
  useEffect(() => {
    if (displayOperatoryList.length > 0 || useMocks) {
      setOperatoriesSettled(true)
      return
    }
    if (!operatoryLoading) {
      setOperatoriesSettled(true)
    }
  }, [displayOperatoryList, operatoryLoading, useMocks])

  const displayAppointmentList = useMemo(() => {
    const list = appointmentList ?? []
    if (providerKey === 'all') return list
    return list.filter((appt) => appt.provider_id === providerKey)
  }, [appointmentList, providerKey])

  const scheduleOperatoryList = useMemo(() => {
    if (providerKey === 'all') return displayOperatoryList
    const usedOperatoryIds = new Set(
      displayAppointmentList
        .map((appt) => appt.operatory_id)
        .filter((id): id is string => Boolean(id)),
    )
    if (usedOperatoryIds.size === 0) return displayOperatoryList
    return displayOperatoryList.filter((operatory) =>
      usedOperatoryIds.has(operatory.operatory_id),
    )
  }, [displayAppointmentList, displayOperatoryList, providerKey])

  // Mount DayCalendar once the host has a real box. Keep it mounted after that
  // so scrolling away does not tear down Syncfusion (which left events blank).
  // Bump visibilityRefreshKey when returning into view so layout can refresh.
  const scheduleHostRef = useRef<HTMLDivElement>(null)
  const [canMountSchedule, setCanMountSchedule] = useState(false)
  const [visibilityRefreshKey, setVisibilityRefreshKey] = useState(0)
  const wasIntersectingRef = useRef(false)
  useEffect(() => {
    if (!enabled) {
      setCanMountSchedule(false)
      wasIntersectingRef.current = false
      return
    }
    const node = scheduleHostRef.current
    if (!node) return

    const isHostReady = (): boolean => {
      const hiddenAncestor = node.closest('[hidden]')
      const { height, width } = node.getBoundingClientRect()
      return !hiddenAncestor && height > 0 && width > 0
    }

    if (isHostReady()) {
      setCanMountSchedule(true)
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry) return
        const hiddenAncestor = node.closest('[hidden]')
        const visible =
          !hiddenAncestor &&
          entry.isIntersecting &&
          entry.intersectionRect.height > 0
        if (visible) {
          setCanMountSchedule(true)
          if (!wasIntersectingRef.current) {
            setVisibilityRefreshKey((key) => key + 1)
          }
          wasIntersectingRef.current = true
          return
        }
        wasIntersectingRef.current = false
      },
      { threshold: 0 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [enabled, operatoriesSettled])

  if (!enabled) return null
  if (!operatoriesSettled) return null

  return (
    <div
      ref={scheduleHostRef}
      className="dashboard-overview-appointments-schedule"
    >
      <div className="calendar-container dashboard-day-calendar-wrap">
        {canMountSchedule ? (
          <DayCalendar
            key={`overview-day-${providerKey}-${
              scheduleOperatoryList.length > 0 ? 'ops' : 'no-ops'
            }`}
            selectDate={date}
            appointmentList={displayAppointmentList}
            providerList={providerList}
            operatoryList={scheduleOperatoryList}
            isHipaa={!!isHIPAAView}
            apptModalToggle={() => undefined}
            setAppointmentDetails={setAppointmentDetails}
            heightOffset={0}
            allowDragAndDrop={false}
            allowResizing={false}
            visibilityRefreshKey={visibilityRefreshKey}
          />
        ) : null}
      </div>
    </div>
  )
}

export const OverviewTodaySchedulePanel = React.memo(
  OverviewTodaySchedulePanelImpl,
)
